// ─────────────────────────────────────────────────────────────
// src/db.js — IndexedDB wrapper for offline POS
// Stores: products, categories, pending_sales, meta
// ─────────────────────────────────────────────────────────────

const DB_NAME = "suqpos_offline";
const DB_VERSION = 1;

let _db = null;

function openDB() {
  if (_db) return Promise.resolve(_db);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (e) => {
      const db = e.target.result;

      // Products store — keyed by product id
      if (!db.objectStoreNames.contains("products")) {
        db.createObjectStore("products", { keyPath: "id" });
      }

      // Categories store — keyed by category id
      if (!db.objectStoreNames.contains("categories")) {
        db.createObjectStore("categories", { keyPath: "id" });
      }

      // Pending sales queue — auto-incremented local id
      if (!db.objectStoreNames.contains("pending_sales")) {
        const store = db.createObjectStore("pending_sales", {
          keyPath: "local_id",
          autoIncrement: true,
        });
        store.createIndex("status", "status", { unique: false });
      }

      // Meta — single-row config store
      if (!db.objectStoreNames.contains("meta")) {
        db.createObjectStore("meta", { keyPath: "key" });
      }
    };

    req.onsuccess = (e) => {
      _db = e.target.result;
      resolve(_db);
    };

    req.onerror = () => reject(req.error);
  });
}

// ── Generic helpers ───────────────────────────────────────────

function tx(storeName, mode, fn) {
  return openDB().then((db) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, mode);
      const store = transaction.objectStore(storeName);
      const req = fn(store);
      if (req) {
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      } else {
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      }
    });
  });
}

function getAll(storeName) {
  return openDB().then((db) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, "readonly");
      const store = transaction.objectStore(storeName);
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  });
}

function putAll(storeName, items) {
  return openDB().then((db) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, "readwrite");
      const store = transaction.objectStore(storeName);
      items.forEach((item) => store.put(item));
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  });
}

// ── Products ──────────────────────────────────────────────────

export async function cacheProducts(products) {
  await putAll("products", products);
  await setMeta("products_cached_at", new Date().toISOString());
}

export function getCachedProducts() {
  return getAll("products");
}

export async function decrementCachedStock(productId, qty) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("products", "readwrite");
    const store = transaction.objectStore("products");
    const req = store.get(productId);
    req.onsuccess = () => {
      const product = req.result;
      if (!product) {
        resolve(false);
        return;
      }
      if (product.stock_qty < qty) {
        resolve(false);
        return;
      } // Option B: block oversell
      product.stock_qty -= qty;
      store.put(product);
      resolve(true);
    };
    req.onerror = () => reject(req.error);
  });
}

export async function restoreCachedStock(productId, qty) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("products", "readwrite");
    const store = transaction.objectStore("products");
    const req = store.get(productId);
    req.onsuccess = () => {
      const product = req.result;
      if (!product) {
        resolve();
        return;
      }
      product.stock_qty += qty;
      store.put(product);
      resolve();
    };
    req.onerror = () => reject(req.error);
  });
}

// ── Categories ────────────────────────────────────────────────

export async function cacheCategories(categories) {
  await putAll("categories", categories);
}

export function getCachedCategories() {
  return getAll("categories");
}

// ── Pending sales ─────────────────────────────────────────────

export async function queueSale(salePayload) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("pending_sales", "readwrite");
    const store = transaction.objectStore("pending_sales");
    const req = store.add({
      ...salePayload,
      status: "pending",
      queued_at: new Date().toISOString(),
      synced_at: null,
      error: null,
    });
    req.onsuccess = () => resolve(req.result); // returns local_id
    req.onerror = () => reject(req.error);
  });
}

export function getPendingSales() {
  return openDB().then((db) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction("pending_sales", "readonly");
      const store = transaction.objectStore("pending_sales");
      const index = store.index("status");
      const req = index.getAll("pending");
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  });
}

// Atomically claims a row for sync by flipping pending -> syncing inside a
// single readwrite transaction (same get-then-compare-then-put pattern as
// decrementCachedStock). IndexedDB serializes readwrite transactions against
// the same object store, so two near-simultaneous callers — e.g. two tabs
// racing to sync after the same reconnect — cannot both see status
// "pending" and both win the claim: whichever transaction's get() runs
// second will see "syncing" (or another status) already written and return
// false instead of re-claiming the row.
export async function claimSaleForSync(localId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("pending_sales", "readwrite");
    const store = transaction.objectStore("pending_sales");
    const req = store.get(localId);
    req.onsuccess = () => {
      const sale = req.result;
      if (!sale || sale.status !== "pending") {
        resolve(false);
        return;
      }
      sale.status = "syncing";
      store.put(sale);
      resolve(true);
    };
    req.onerror = () => reject(req.error);
  });
}

// Resets any row stuck in "syncing" back to "pending" so it becomes
// eligible for another sync attempt. A row can only be left "syncing" if
// the app closed/crashed while its process_sale call was in flight (a
// normal sync always resolves a claimed row to "synced" or "failed") — see
// the call site in App.js for the double-charge tradeoff this implies.
export async function recoverStuckSyncingSales() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("pending_sales", "readwrite");
    const store = transaction.objectStore("pending_sales");
    const index = store.index("status");
    const req = index.getAll("syncing");
    req.onsuccess = () => {
      const stuck = req.result;
      stuck.forEach((sale) => {
        sale.status = "pending";
        store.put(sale);
      });
      resolve(stuck.length);
    };
    req.onerror = () => reject(req.error);
  });
}

export function getAllPendingSales() {
  return getAll("pending_sales");
}

export async function updateSaleStatus(localId, status, error = null) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("pending_sales", "readwrite");
    const store = transaction.objectStore("pending_sales");
    const req = store.get(localId);
    req.onsuccess = () => {
      const sale = req.result;
      if (!sale) {
        resolve();
        return;
      }
      sale.status = status;
      sale.error = error;
      sale.synced_at = status === "synced" ? new Date().toISOString() : null;
      store.put(sale);
      resolve();
    };
    req.onerror = () => reject(req.error);
  });
}

export async function getPendingCount() {
  const pending = await getPendingSales();
  return pending.length;
}

// ── Meta ──────────────────────────────────────────────────────

export async function setMeta(key, value) {
  return tx("meta", "readwrite", (store) => store.put({ key, value }));
}

export async function getMeta(key) {
  return openDB().then((db) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction("meta", "readonly");
      const store = transaction.objectStore("meta");
      const req = store.get(key);
      req.onsuccess = () => resolve(req.result?.value ?? null);
      req.onerror = () => reject(req.error);
    });
  });
}
